import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { Calendar, DollarSign, User, Eye } from 'lucide-react';
import { Deal } from '@/hooks/useDeals';
import { format } from 'date-fns';

interface DealCardProps {
  deal: Deal;
  progress: number;
  onViewDetails: (deal: Deal) => void;
}

export const DealCard: React.FC<DealCardProps> = ({ deal, progress, onViewDetails }) => {
  // Defensive fallback for task count
  const taskCount = (deal as any).tasks?.length ?? (deal as any).deal_tasks?.length ?? 0;

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active': return 'bg-green-500/10 text-green-700 border-green-200';
      case 'pending': return 'bg-yellow-500/10 text-yellow-700 border-yellow-200';
      case 'completed': return 'bg-blue-500/10 text-blue-700 border-blue-200';
      case 'cancelled': return 'bg-red-500/10 text-red-700 border-red-200';
      default: return 'bg-gray-500/10 text-gray-700 border-gray-200';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority.toLowerCase()) {
      case 'high': return 'bg-red-500/10 text-red-700 border-red-200';
      case 'medium': return 'bg-yellow-500/10 text-yellow-700 border-yellow-200';
      case 'low': return 'bg-green-500/10 text-green-700 border-green-200';
      default: return 'bg-gray-500/10 text-gray-700 border-gray-200';
    }
  };

  return (
    <Card className="hover:shadow-lg transition-shadow">
      <CardHeader className="pb-3">
        <div className="flex justify-between items-start">
          <CardTitle className="text-lg font-semibold">{deal.title}</CardTitle>
          <div className="flex gap-2">
            <Badge variant="secondary" className={getStatusColor(deal.status)}>
              {deal.status}
            </Badge>
            <Badge variant="outline" className={getPriorityColor(deal.priority)}>
              {deal.priority}
            </Badge>
          </div>
        </div>
        {deal.description && (
          <p className="text-sm text-muted-foreground line-clamp-2">
            {deal.description}
          </p>
        )}
      </CardHeader>
      
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span>Progress</span>
            <span>{progress}%</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        <div className="grid grid-cols-2 gap-4 text-sm">
          <div className="flex items-center gap-2">
            <DollarSign className="h-4 w-4 text-muted-foreground" />
            <span>${deal.deal_value.toLocaleString()}</span>
          </div>
          
          <div className="flex items-center gap-2">
            <User className="h-4 w-4 text-muted-foreground" />
            <span className="capitalize">{deal.deal_type}</span>
          </div>
          
          {deal.estimated_completion_date && (
            <div className="flex items-center gap-2 col-span-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <span>Due: {format(new Date(deal.estimated_completion_date), 'MMM dd, yyyy')}</span>
            </div>
          )}
        </div>

        <div className="flex justify-between items-center pt-2">
          <div className="text-xs text-muted-foreground">
            {taskCount} tasks
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onViewDetails(deal)}
            className="flex items-center gap-2"
          >
            <Eye className="h-4 w-4" />
            View Details
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};